import PropTypes from 'prop-types';
import React from 'react';
import { List } from 'immutable';
import classnames from 'classnames';
import _ from 'lodash';

import { translate as t } from '../../lib/I18n';
import Station from '../../records/Station';
import Search from '../../records/Search';
import SeasonSearch from '../../records/SeasonSearch';
import config from '../../config';
import StationFinderStore from '../../stores/StationFinderStore';
import StationFinderActions from '../../actions/StationFinderActions';
import NotificationActions from '../../actions/NotificationActions';
import connectToStores from '../../helpers/connectToStores';

import MapComponent from '../Map';
import SearchPath from '../map/SearchPath';
import StationSearchOverlay from './SearchOverlay';
import StationMarker from './StationMarker';
import StationFinderModal from './StationFinderModal';

export class StationFinder extends React.PureComponent {
    state = {
        selectedStation: null,
        showStationFinderModal: false,
        stationFinderActive: false,
        isSearchingStations: false,
        ticketOffice: null,
        loadingTicketOfficeDetails: false
    };

    static propTypes = {
        className: PropTypes.string,
        origin: PropTypes.instanceOf(Station),
        destination: PropTypes.instanceOf(Station),
        stations: PropTypes.instanceOf(List),
        error: PropTypes.instanceOf(Error),
        isExpanded: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        isMobile: PropTypes.bool.isRequired,
        closeContainerModal: PropTypes.func,
        currentSearch: PropTypes.oneOfType([
            PropTypes.instanceOf(Search),
            PropTypes.instanceOf(SeasonSearch)
        ]),
    }

    positionSelectedMarker = null;

    UNSAFE_componentWillMount() {
        if (this.props.stations.size) {
            this._clearSuggestedStationMarkers();
        }
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.origin !== this.props.origin || newProps.destination !== this.props.destination) {
            this.setState({ stationFinderActive: false });
            this._clearSuggestedStationMarkers();
            this._removeSelectedPositionMarker();
            if (this.props.isExpanded) {
                StationFinderActions.setExpanded(false);
            }
        }

        if (newProps.stations.size > 0 || newProps.error) {
            this.setState({ isSearchingStations: false });
            this._resizeAndFitBounds();
        }
    }

    _clearSuggestedStationMarkers() {
        _.defer(StationFinderActions.clearStations);
    }

    _getGBoundsFromCoords = (bounds) => {
        const gBounds = new window.google.maps.LatLngBounds();
        bounds.forEach((latLng) => gBounds.extend(latLng));
        return gBounds;
    }

    _resizeAndFitBounds = () => {
        const { mapsHelper } = this;
        const bounds = StationFinderStore.getBounds();

        mapsHelper.resize();

        if (bounds.length > 0) {
            mapsHelper.map.fitBounds(this._getGBoundsFromCoords(bounds));
        }
    }

    onSelectPosition = (pos) => {
        const { mapsHelper } = this;

        this.setState({ stationFinderActive: true });

        this._removeSelectedPositionMarker();

        if (this.props.stations.size) {
            this._clearSuggestedStationMarkers();
        }

        const icon = {
            url: `/images/station-marker-${config.get('brand').split('-')[0]}.svg`,
            fillOpacity: 1,
            scaledSize: new window.google.maps.Size(50, 50)
        };

        this.positionSelectedMarker = mapsHelper.addMarker({
            position: pos,
            icon: icon,
            name: 'locationSelected',
            title: 'Your Position',
            animation: 'DROP',
            draggable: true,
        });

        mapsHelper.setCenter(pos);
        this.positionSelectedMarker.addListener('dragend', (e) => { this.resetSelectedPosition(e); });
        this.positionSelectedMarker.addListener('click', () => { this._resizeAndFitBounds(); });
     }

     resetSelectedPosition = (e) => {
         const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
         this.onSelectPosition(latLng);
         this.handleSearch(latLng);
     }

    _removeSelectedPositionMarker() {
        if (this.positionSelectedMarker) {
            this.positionSelectedMarker.setMap(null);
            this.positionSelectedMarker = null;
        }
    }

    zoomToPosition = (pos) => {
        const { mapsHelper } = this;
        mapsHelper.setCenter(pos);
        mapsHelper.setZoomLevel(17);
    }

    handleSearchClear = () => {
        this._removeSelectedPositionMarker();
        this.setState({ stationFinderActive: false });

        if (this.props.stations.size > 0) {
            this._clearSuggestedStationMarkers();
        }
    }

    handleSearch = ({ lat: latitude, lng: longitude }, searchInteraction) => {
        this.setState({ isSearchingStations: true });
        StationFinderActions.findNearest({ latitude, longitude, searchInteraction });
    }

    handleMapReady = (mapsHelper) => {
        this.mapsHelper = mapsHelper;
    }

    handleStationSelected = async (station) => {
        this._resizeAndFitBounds();
        this.setState({
            showStationFinderModal: true,
            selectedStation: station,
            ticketOffice: null,
            loadingTicketOfficeDetails: true
        });

        try {
            const ticketOffice = await StationFinderActions.getTicketOfficeDetails(station);
            this.setState(
                (state) => state.selectedStation === station ? { ticketOffice } : {}
            );
        } catch (e) {
            console.warn(`error loading opening hours`, e);
        }
        this.setState({ loadingTicketOfficeDetails: false });
    }

    handleStationFinderModalClose = () => {
        this.setState({ showStationFinderModal: false });
    }

    _handleStationChange = (type, value) => {
        StationFinderActions.setStationSelected(type, value);

        if (this.props.isMobile) {
            this._clearSuggestedStationMarkers();
        }

        this.props.onChange(type, value);

        if (this.props.isMobile) {
            const updatedText = `${_.capitalize(type)} ${t('stationFinder.modal.update')} ${value.name}`;
            NotificationActions.showNotification(updatedText);
            this.props.closeContainerModal();
        }
    };

    _getContainerClassName() {
        return classnames('stationFinder__container', {
            'mobile': this.props.isMobile
        });
    }

    render () {
        const { origin, destination, stations, currentSearch } = this.props;
        const {
            stationFinderActive,
            isSearchingStations,
            selectedStation,
            ticketOffice,
            showStationFinderModal,
            loadingTicketOfficeDetails
        } = this.state;

        return (
            <div className={ this._getContainerClassName() }>
                <MapComponent allowMapExpand={ !this.props.isMobile } className={ 'map ' + this.props.className } stationFinderActive={ stationFinderActive } onReady={ this.handleMapReady }>
                    <StationSearchOverlay
                        error={ this.props.error }
                        isExpanded={ this.props.isExpanded }
                        onClear={ this.handleSearchClear }
                        isMobile={ this.props.isMobile }
                        onSelectPosition={ this.onSelectPosition }
                        onSearch={ this.handleSearch }
                        isSearchingStations={ isSearchingStations }/>
                    <SearchPath key='search' origin={ origin } destination={ destination } />
                    <StationFinderModal
                        selectedStation={ selectedStation }
                        ticketOffice={ ticketOffice }
                        show={ showStationFinderModal }
                        onClose={ this.handleStationFinderModalClose }
                        onContinue={ this._handleStationChange }
                        loadingTicketOfficeDetails={ loadingTicketOfficeDetails }
                        currentSearch={ currentSearch }/>
                    { stations &&
                        stations.map((station) => <StationMarker key={ station.nlc } station={ station } onSelect={ this.handleStationSelected } />) }
                </MapComponent>
            </div>
        );
    }
}

function getStateFromStores () {
    return {
        stations: StationFinderStore.get('stations'),
        error: StationFinderStore.get('error'),
        isExpanded: StationFinderStore.get('isExpanded'),
    };
}

export default connectToStores([ StationFinderStore ], getStateFromStores)(StationFinder);



// WEBPACK FOOTER //
// ./src/components/stationFinder/index.js